import { useEffect, useState } from "react";
import client from "../lib/api";
import { money } from "../lib/helpers";
import { TrendUp, TrendDown, Clock, Scales, ArrowDown, ArrowUp } from "@phosphor-icons/react";

const BUCKET_LABELS = { current: "Current", "1-30": "1–30 days", "31-60": "31–60 days", "61-90": "61–90 days", "90+": "90+ days" };
const BUCKET_COLORS = { current: "#2e7d32", "1-30": "#B08D57", "31-60": "#c47f1a", "61-90": "#d1620e", "90+": "#E01E37" };

const FIN_TONES = {
  emerald: { cls: "surface-kpi--emerald", medal: "bg-[#E6EFEA] text-[#1A4331] border-[#2D6A4F]/30", value: "#1A4331" },
  crimson: { cls: "surface-kpi--crimson", medal: "bg-[#F0E6E6] text-[#7A0000] border-[#7E1119]/30", value: "#7A0000" },
  bronze: { cls: "surface-kpi--bronze", medal: "bg-[#F5ECE3] text-[#8A5028] border-[#B87333]/35", value: "#8A5028" },
  sapphire: { cls: "surface-kpi--sapphire", medal: "bg-[#E6EAEF] text-[#12233D] border-[#2B4C7E]/30", value: "#12233D" },
  oxblood: { cls: "surface-kpi--oxblood-accent", medal: "bg-[#4A0000]/8 text-[#4A0000] border-[#4A0000]/20", value: "#4A0000" },
};

function Kpi({ icon: Icon, label, value, sub, variant = "oxblood", testid }) {
  const t = FIN_TONES[variant] || FIN_TONES.oxblood;
  return (
    <div className={`surface-kpi ${t.cls} p-5 flex flex-col gap-2`} data-testid={testid}>
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-2 text-[#5C5A58] eyebrow text-xs">{label}</div>
        <span className={`h-8 w-8 rounded-full flex items-center justify-center border ${t.medal}`}><Icon size={15} weight="bold" /></span>
      </div>
      <p className="font-display text-3xl relative z-10" style={{ color: t.value }}>{value}</p>
      {sub && <p className="text-xs text-[#5C5A58] relative z-10">{sub}</p>}
    </div>
  );
}

export default function FinanceOverview() {
  const [o, setO] = useState(null);
  const [aging, setAging] = useState(null);

  useEffect(() => {
    client.get("/finance/overview").then((r) => setO(r.data)).catch(() => {});
    client.get("/finance/receivables/aging").then((r) => setAging(r.data.buckets)).catch(() => {});
  }, []);

  if (!o) return <p className="text-[#5C5A58]">Loading finance overview…</p>;

  const agingTotal = aging ? Object.values(aging).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-6" data-testid="finance-overview">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi testid="kpi-ar-outstanding" icon={ArrowDown} label="Receivable (owed to you)" value={money(o.ar_outstanding)} variant="emerald" sub={`${o.ar_count} open invoice${o.ar_count === 1 ? "" : "s"}`} />
        <Kpi testid="kpi-ap-outstanding" icon={ArrowUp} label="Payable (you owe photographers)" value={money(o.ap_outstanding)} variant="bronze" />
        <Kpi testid="kpi-net" icon={Scales} label="Net position" value={money(o.net_position)} variant={o.net_position >= 0 ? "emerald" : "crimson"} sub="Receivable − Payable" />
        <Kpi testid="kpi-overdue" icon={Clock} label="Overdue receivables" value={money(o.ar_overdue)} variant={o.ar_overdue > 0 ? "crimson" : "sapphire"} sub={`${o.ar_overdue_count} past due`} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Kpi testid="kpi-collected" icon={TrendUp} label={`Collected in ${o.month_label}`} value={money(o.ar_collected_month)} variant="emerald" />
        <Kpi testid="kpi-paidout" icon={TrendDown} label={`Paid out in ${o.month_label}`} value={money(o.ap_paid_month)} variant="sapphire" />
      </div>

      {aging && (
        <div className="lux-panel p-5" data-testid="ar-aging">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg">Receivables aging</h3>
            <span className="text-sm text-[#5C5A58]">Outstanding {money(agingTotal)}</span>
          </div>
          <div className="space-y-3">
            {Object.keys(BUCKET_LABELS).map((k) => {
              const v = aging[k] || 0;
              const pct = agingTotal > 0 ? (v / agingTotal) * 100 : 0;
              return (
                <div key={k} data-testid={`aging-${k}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#5C5A58]">{BUCKET_LABELS[k]}</span>
                    <span className="font-mono-num font-medium">{money(v)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#F0ECE2] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: BUCKET_COLORS[k] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
